/* Fuego & Fragancia — Marquee de la announcement bar.

   Dos responsabilidades:

   1. Continuidad real. El bucle solo se ve infinito si el contenido repetido
      es más ancho que la pantalla. Con pocos mensajes eso no se cumple y
      aparece un hueco, así que aquí se mide un bloque y se clona las veces
      necesarias para cubrir siempre el doble del viewport.

   2. Red de seguridad. Si la animación CSS no llega a ejecutarse (ajuste de
      "reducir animaciones", alguna extensión, un CSS que la pise), se
      reproduce con requestAnimationFrame para que nunca quede congelado. */

if (!customElements.get('ff-marquee')) {
  customElements.define(
    'ff-marquee',
    class FfMarquee extends HTMLElement {
      // Ancho de referencia para que la velocidad se perciba igual
      // independientemente de cuántos mensajes haya configurados.
      static REFERENCE_WIDTH = 1400;

      connectedCallback() {
        // Selectores configurables: el mismo marquee sirve para la barra de
        // anuncios y para el carrusel de logos del trust bar. Si no se indican,
        // se usan los de la barra de anuncios (comportamiento anterior).
        this.trackSel = this.dataset.track || '.ff-announcement-bar__track';
        this.setSel = this.dataset.set || '.ff-announcement-bar__track-set';

        this.track = this.querySelector(this.trackSel);
        if (!this.track) return;

        this.secondsPerLoop = parseFloat(this.dataset.speed) || 26;
        this.respectReducedMotion = this.dataset.respectReducedMotion === 'true';

        this.build();

        // Las fuentes cambian el ancho del texto al cargar: hay que volver a
        // medir, o el número de copias se calcula sobre la fuente de reserva.
        if (document.fonts && document.fonts.ready) {
          document.fonts.ready.then(() => this.build());
        }

        // Al cambiar el ancho de ventana hay que recalcular las copias.
        this.resizeObserver = new ResizeObserver(() => this.build());
        this.resizeObserver.observe(this);
      }

      build() {
        const set = this.track.querySelector(this.setSel);
        if (!set) return;

        const setWidth = set.scrollWidth;
        if (!setWidth) return;

        // Copias necesarias para que el track siempre exceda el doble del
        // ancho visible: así al reiniciar el ciclo nunca se ve el final.
        const needed = Math.max(2, Math.ceil((window.innerWidth * 2) / setWidth));
        const current = this.track.querySelectorAll(this.setSel).length;

        if (needed > current) {
          const fragment = document.createDocumentFragment();
          for (let i = current; i < needed; i += 1) {
            const clone = set.cloneNode(true);
            clone.setAttribute('aria-hidden', 'true');
            fragment.appendChild(clone);
          }
          this.track.appendChild(fragment);
        }

        this.setWidth = setWidth;

        // El desplazamiento es exactamente un bloque, no un porcentaje del
        // track: así el punto de reinicio coincide siempre con el original.
        this.track.style.setProperty('--ff-marquee-shift', `${setWidth}px`);

        // Duración proporcional al ancho para mantener los píxeles por
        // segundo constantes con cualquier número de mensajes.
        const duration = (this.secondsPerLoop * setWidth) / FfMarquee.REFERENCE_WIDTH;
        this.track.style.animationDuration = `${Math.max(duration, 2)}s`;

        if (this.respectReducedMotion && this.prefersReducedMotion()) {
          this.track.style.animationPlayState = 'paused';
          return;
        }

        if (!this.usingFallback) {
          requestAnimationFrame(() => this.ensureRunning());
        }
      }

      prefersReducedMotion() {
        return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      }

      isCssAnimationRunning() {
        if (typeof this.track.getAnimations !== 'function') return false;
        return this.track.getAnimations().some((animation) => {
          return animation.playState === 'running' || animation.playState === 'finished';
        });
      }

      ensureRunning() {
        if (this.isCssAnimationRunning()) return;
        this.startJsFallback();
      }

      startJsFallback() {
        this.usingFallback = true;
        this.track.style.animation = 'none';

        let offset = 0;
        let last = performance.now();

        const step = (now) => {
          const delta = (now - last) / 1000;
          last = now;

          const shift = this.setWidth;
          if (shift > 0) {
            const pxPerSecond = FfMarquee.REFERENCE_WIDTH / this.secondsPerLoop;
            offset = (offset + pxPerSecond * delta) % shift;
            this.track.style.transform = `translateX(${-offset}px)`;
          }

          this.raf = requestAnimationFrame(step);
        };

        this.raf = requestAnimationFrame(step);
      }

      disconnectedCallback() {
        if (this.raf) cancelAnimationFrame(this.raf);
        if (this.resizeObserver) this.resizeObserver.disconnect();
      }
    }
  );
}
